import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Sparkles } from "lucide-react";
import { useCart } from "../context/AppContext";
import { useSubscribe, WELCOME_SEEN_KEY } from "./marketing/useSubscribe";
import { ModalShell, PromoCodeBox, EmailCaptureForm } from "./marketing/ModalParts";

const SESSION_DISMISS_KEY = "bl_welcome_dismissed";
const DELAY_MS = 8000;

// Fires after DELAY_MS or on desktop exit-intent; skipped if claimed before, dismissed this session, or cart non-empty.
function useWelcomeTrigger(cartCount, onFire) {
  useEffect(() => {
    if (localStorage.getItem(WELCOME_SEEN_KEY)) return;
    if (sessionStorage.getItem(SESSION_DISMISS_KEY)) return;
    if (cartCount > 0) return;

    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      onFire();
      cleanup();
    };
    const exitHandler = (e) => { if (e.clientY < 0) fire(); };
    const timer = setTimeout(fire, DELAY_MS);
    document.addEventListener("mouseout", exitHandler);
    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", exitHandler);
    };
    return cleanup;
  }, [cartCount, onFire]);
}

export default function WelcomeModal() {
  const { items } = useCart();
  const [open, setOpen] = useState(false);
  const { email, setEmail, submitting, result, submit, copyCode } = useSubscribe("welcome_popup", "Couldn't sign you up — try again");

  const show = useCallback(() => setOpen(true), []);
  useWelcomeTrigger(items.length, show);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
  };

  if (!open) return null;

  return (
    <ModalShell testid="welcome-modal" zIndex="z-[100]" borderClass="border-[color:var(--bl-gold)]/25" onClose={close} closeTestid="welcome-close">
      {!result ? (
        <>
          <div className="inline-flex items-center gap-2 text-[color:var(--bl-gold)]">
            <Sparkles size={14} />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Inner Circle</span>
          </div>
          <h2 className="font-display text-white text-4xl md:text-5xl mt-3 leading-none tracking-tight uppercase">
            5% off
            <br /><span className="text-[color:var(--bl-gold)]">your first order</span>
          </h2>
          <p className="text-white/60 text-sm mt-5 leading-relaxed max-w-md">
            Drop your email for a one-time welcome code, early access to new peptides, and quiet drops before the public sees them.
          </p>
          <EmailCaptureForm
            email={email} setEmail={setEmail} submitting={submitting} onSubmit={submit} onSkip={close}
            prefix="welcome" submitLabel="Send Me the Code" skipLabel="No thanks, I'll pay full price" buttonClass="primary"
          />
          <p className="font-mono text-[10px] tracking-[0.15em] text-white/30 mt-6 leading-relaxed">
            For research use only. Unsubscribe anytime. We never share your email.
          </p>
        </>
      ) : (
        <div data-testid="welcome-success">
          <CheckCircle2 size={40} className="text-[color:var(--bl-blue)]" />
          <div className="bl-eyebrow text-[color:var(--bl-gold)] mt-6">You're In</div>
          <h2 className="font-display text-white text-4xl mt-3 uppercase">Your Code</h2>
          <PromoCodeBox result={result} onCopy={copyCode} testid="welcome-code-box" caption="at checkout" />
          <p className="text-white/55 text-sm mt-6 leading-relaxed">
            Copy it, then paste it into the promo field in cart. We'll also drop it in your inbox.
          </p>
          <button onClick={close} data-testid="welcome-done" className="mt-6 w-full bl-btn primary py-3">
            Start Shopping
          </button>
        </div>
      )}
    </ModalShell>
  );
}
